import { HttpResponse } from "@angular/common/http";
import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { fuseAnimations } from "@fuse/animations";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { DeviceModel, DeviceType } from "app/models/device.model";
import { ManufacturerModel } from "app/models/manufacturer.model";
import { Protocol } from "app/models/protocol.model";
import { DeviceService } from "app/services/api/device.service";
import { ManufacturerService } from "app/services/api/manufacturer.service";
import { ToastrService } from "ngx-toastr";
import * as moment from "moment";
import { timer } from "rxjs";
import { debounce } from "rxjs/operators";
import { locale as catalan } from "../../i18n/ca";
import { locale as english } from "../../i18n/en";
import { locale as spanish } from "../../i18n/es";
import { FileSystemFileEntry, NgxFileDropEntry } from "ngx-file-drop";
import { UploadUtils } from "app/shared/upload.utils";
import { Cloudinary } from "@cloudinary/url-gen";
import { CloudinaryImage } from "@cloudinary/url-gen/assets/CloudinaryImage";
import { fill, limitFit } from "@cloudinary/url-gen/actions/resize";
import { DeviceFileModel, DeviceFileSource, DeviceFileStatus } from "app/models/device.model";
import { PageEvent } from "@angular/material/paginator";
import { DeviceFilesRequest, DeviceFileSourceFilter } from "app/services/api/interfaces/request.interface";
import { PaginationResponse } from "app/services/api/interfaces/response.interface";
import { DeviceManufacturerCatalogEntry, DeviceModelCatalogEntry, DEVICE_MODEL_CATALOG } from "./device-model-catalog.config";

type FileFilterDateValue = Date | moment.Moment | null;
type ResolvedFileFilters = {
    source: DeviceFileSourceFilter | null;
    startDate: Date | null;
    endDate: Date | null;
};
type DeviceTextField = "name" | "model" | "serialNumber" | "simOrImei";

@Component({
    selector: "app-device-admin",
    templateUrl: "./deviceAdmin.component.html",
    styleUrls: ["./deviceAdmin.component.scss"],
    animations: fuseAnimations,
    standalone: false,
})
export class DeviceAdminComponent implements OnInit {
    private readonly cloudinary = new Cloudinary({ cloud: { cloudName: "emmadb" } });
    private readonly cloudinaryImageCache = new Map<string, CloudinaryImage>();
    private readonly deviceModelCatalog = DEVICE_MODEL_CATALOG;

    device: DeviceModel;
    deviceForm: UntypedFormGroup;
    fileFiltersForm: UntypedFormGroup;
    error: string;
    pageType: string;
    manufacturers: ManufacturerModel[];
    selectedManufacturer: ManufacturerModel;
    availableModels: DeviceModelCatalogEntry[] = [];
    updatingModel: boolean;
    savedChangesString: string;
    creationSuccessString: string;
    avaliableDeviceType = [DeviceType.NoiseSensor, DeviceType.AcousticLimiter];
    editImage: boolean;

    public uploadedFile: File = undefined;
    public imageUrl: any = undefined;
    public fileDisplayedColumns = ["select", "name", "size", "creationDate", "uploadDate", "actions"];
    public files: DeviceFileModel[] = [];
    public filesTotal = 0;
    public filesPageSize = 25;
    public filesPageIndex = 0;
    public readonly selectedFileIds = new Set<string>();
    public readonly reprocessingFileIds = new Set<string>();
    public rescanningFileStorage = false;
    private readonly selectedFileRevisions = new Map<string, number>();
    private selectionRevisionCounter = 0;

    constructor(
        private deviceService: DeviceService,
        private manufacturerService: ManufacturerService,
        private route: ActivatedRoute,
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private translation: FuseTranslationLoaderService,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef,
    ) {
        this.translation.loadTranslations(english, spanish, catalan);
        this.pageType = "new";

        translation.translateService.get("GLOBAL", { value: "SavedChanges" }).subscribe((res) => {
            this.savedChangesString = res.SavedChanges;
        });
        translation.translateService.get("GLOBAL", { value: "CreatedSuccess" }).subscribe((res) => {
            this.creationSuccessString = res.CreatedSuccess;
        });
    }

    ngOnInit() {
        this.device = this.route.snapshot.data.data;
        if (this.device.id) {
            this.pageType = "edit";
        }

        this.createForm();
        this.createFileFiltersForm();
        this.updateModel();
        this.updateManufacturerData();
        this.updateForm();
        this.loadFiles();
    }

    add() {
        const deviceSave = this.createSaveDevice();

        this.deviceService.create(deviceSave).subscribe(
            () => {
                this.toastr.success(this.creationSuccessString);
                this.router.navigate(["/admin/devices"]);
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    save() {
        const deviceEdit = this.createEditDevice();

        this.deviceService.edit(deviceEdit).subscribe(
            () => {
                this.toastr.success(this.savedChangesString);
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                if (error.error.statusCode === 404) {
                    this.router.navigate(["/admin/devices"]);
                }
            },
        );
    }

    cancel() {
        this.router.navigate(["/admin/devices"]);
    }

    removeImage() {
        this.uploadedFile = undefined;
        this.imageUrl = undefined;

        this.editImage = true;

        this.deviceService.removeImage(this.device.id).subscribe(
            () => {
                this.toastr.success(this.savedChangesString);
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                if (error.error.statusCode === 404) {
                    this.router.navigate(["/admin/devices"]);
                }
            },
        );
    }

    imageDropped(files: NgxFileDropEntry[]) {
        this.editImage = true;

        const uploadedImage = files[0];
        // Check image
        // Is it a file?
        if (uploadedImage.fileEntry.isFile) {
            const fileEntry = uploadedImage.fileEntry as FileSystemFileEntry;
            fileEntry.file((file) => {
                if (!UploadUtils.isFileAllowed(file)) {
                    alert("File is not a supported image");
                    return;
                }
                if (!UploadUtils.isFileSizeAllowed(file)) {
                    alert("File Size is larger than 2 MB");
                    return;
                }
                this.uploadedFile = file;

                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    this.imageUrl = reader.result;
                };

                this.deviceService.addImage(this.device.id, this.uploadedFile).subscribe(
                    () => {
                        this.toastr.success(this.savedChangesString);
                    },
                    (error) => {
                        console.log(error);
                        this.toastr.error("Error: " + error.message);
                        if (error.error.statusCode === 404) {
                            this.router.navigate(["/admin/devices"]);
                        }
                    },
                );
            });
        } else {
            // It was a directory (nothing to be done)
        }
    }

    downloadFile(file: DeviceFileModel) {
        const downloadWindow = window.open("", "_blank");

        if (!downloadWindow) {
            this.toastr.error("Please allow popups to download files.");
            return;
        }

        downloadWindow.opener = null;

        this.deviceService.getFileDownloadUrl(this.device.id, file.id).subscribe(
            ({ url }) => {
                downloadWindow.location.href = url;
            },
            (error) => {
                console.log(error);
                downloadWindow.close();
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    reprocessFile(file: DeviceFileModel) {
        if (!this.canReprocessFile(file)) {
            return;
        }

        this.reprocessingFileIds.add(file.id);
        this.deviceService.reprocessFile(this.device.id, file.id).subscribe(
            (updatedFile) => {
                this.toastr.success(this.translation.translateService.instant("DEVICES.ReprocessFileQueued"));
                this.reprocessingFileIds.delete(file.id);
                this.updateFileInList(updatedFile);
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.reprocessingFileIds.delete(file.id);
                this.toastr.error("Error: " + error.message);
                this.cdr.detectChanges();
            },
        );
    }

    rescanFileStorage() {
        if (this.rescanningFileStorage) {
            return;
        }

        this.rescanningFileStorage = true;
        this.deviceService.rescanFileStorage().subscribe(
            (result) => {
                this.rescanningFileStorage = false;
                this.toastr.success(
                    this.translation.translateService.instant("DEVICES.RescanFileStorageCompleted", {
                        count: result.recoveredFiles,
                    }),
                );
                this.loadFiles();
            },
            (error) => {
                console.log(error);
                this.rescanningFileStorage = false;
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    downloadSelectedFiles() {
        if (!this.device?.id || this.selectedFileIds.size === 0) {
            return;
        }

        const requestedFileIds = Array.from(this.selectedFileIds);
        const requestedSelectionRevisions = new Map(requestedFileIds.map((fileId) => [fileId, this.selectedFileRevisions.get(fileId)]));

        this.deviceService.downloadFilesZip(this.device.id, requestedFileIds).subscribe(
            (response: HttpResponse<Blob>) => {
                const blob = response.body;
                if (!blob) {
                    this.toastr.error("Error: ZIP response is empty");
                    return;
                }

                this.triggerBlobDownload(blob, this.getZipFilename(response.headers.get("content-disposition")));
                requestedFileIds.forEach((fileId) => {
                    if (
                        this.selectedFileIds.has(fileId) &&
                        this.selectedFileRevisions.get(fileId) === requestedSelectionRevisions.get(fileId)
                    ) {
                        this.selectedFileIds.delete(fileId);
                        this.selectedFileRevisions.delete(fileId);
                    }
                });
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    handleFilesPage(event: PageEvent) {
        this.filesPageIndex = event.pageIndex;
        this.filesPageSize = event.pageSize;
        this.loadFiles();
    }

    handleFileFiltersChange() {
        const filters = this.resolveFileFilters();
        this.filesPageIndex = 0;
        this.resetFileSelection();
        this.loadFiles(filters);
    }

    clearFileFilters() {
        this.fileFiltersForm.reset(undefined, { emitEvent: false });
        this.handleFileFiltersChange();
    }

    toggleFileSelection(fileId: string, checked: boolean) {
        if (checked) {
            this.selectedFileIds.add(fileId);
            this.selectionRevisionCounter += 1;
            this.selectedFileRevisions.set(fileId, this.selectionRevisionCounter);
            return;
        }

        this.selectedFileIds.delete(fileId);
        this.selectedFileRevisions.delete(fileId);
    }

    toggleCurrentPageSelection(checked: boolean) {
        this.files.forEach((file) => this.toggleFileSelection(file.id, checked));
    }

    isFileSelected(fileId: string): boolean {
        return this.selectedFileIds.has(fileId);
    }

    areAllFilesOnPageSelected(): boolean {
        return this.files.length > 0 && this.files.every((file) => this.selectedFileIds.has(file.id));
    }

    hasSomeFilesOnPageSelected(): boolean {
        return this.files.some((file) => this.selectedFileIds.has(file.id)) && !this.areAllFilesOnPageSelected();
    }

    get selectedFilesCount(): number {
        return this.selectedFileIds.size;
    }

    get hasActiveFileFilters(): boolean {
        const filters = this.resolveFileFilters();
        return !!filters.source || !!filters.startDate || !!filters.endDate;
    }

    get isFileDateRangeInvalid(): boolean {
        return this.hasInvalidFileDateRange(this.resolveFileFilters());
    }

    formatFileSize(size?: number): string {
        if (!size || size <= 0) {
            return "—";
        }

        const units = ["B", "KB", "MB", "GB", "TB"];
        let value = size;
        let unitIndex = 0;
        while (value >= 1024 && unitIndex < units.length - 1) {
            value /= 1024;
            unitIndex++;
        }

        const maximumFractionDigits = unitIndex === 0 ? 0 : value >= 10 ? 1 : 2;
        return `${value.toLocaleString(undefined, { maximumFractionDigits })} ${units[unitIndex]}`;
    }

    canReprocessFile(file: DeviceFileModel): boolean {
        return (
            file?.source === DeviceFileSource.SvantekSVL &&
            !this.reprocessingFileIds.has(file.id) &&
            file.status !== DeviceFileStatus.PendingReprocess &&
            file.status !== DeviceFileStatus.Reprocessing
        );
    }

    private updateFileInList(updatedFile: DeviceFileModel) {
        this.files = this.files.map((file) => (file.id === updatedFile.id ? updatedFile : file));
    }

    get imageCloudinaryImage(): CloudinaryImage | null {
        return this.getCloudinaryImage(this.imageUrl, 800, 600, "limit");
    }

    /**
     * Private methods
     */
    private createEditDevice() {
        const deviceEdit = Object.assign(new DeviceModel(), this.deviceForm.getRawValue());
        delete deviceEdit.manufacturer;
        delete deviceEdit.model;
        delete deviceEdit.serialNumber;
        delete deviceEdit.protocol;
        delete deviceEdit.picture;
        return deviceEdit;
    }

    private createSaveDevice() {
        const deviceSave = Object.assign(this.device, this.deviceForm.getRawValue());
        this.trimDeviceTextFields(deviceSave);
        deviceSave.manufacturer = deviceSave.manufacturer.id;
        return deviceSave;
    }

    private trimDeviceTextFields(device: DeviceModel) {
        const textFields: DeviceTextField[] = ["name", "model", "serialNumber", "simOrImei"];

        textFields.forEach((field) => {
            const value = device[field];
            if (typeof value === "string") {
                device[field] = value.trim();
            }
        });
    }

    private createForm() {
        this.deviceForm = this.formBuilder.group({
            id: [this.device.id],
            name: [this.device.name, [Validators.required]],
            model: [this.device.model, [Validators.required]],
            serialNumber: [this.device.serialNumber, [Validators.required]],
            dataFrequency: [this.device.dataFrequency, [Validators.required, Validators.min(0)]],
            deviceType: [this.device.deviceType, [Validators.required]],
            manufacturer: [null, [Validators.required]],
            protocol: [null, [Validators.required]],
            simOrImei: [this.device.simOrImei],
            lastCalibrationDate: [this.device.lastCalibrationDate],
        });

        if (this.pageType === "new") {
            this.deviceForm.get("model").disable();
            this.deviceForm.get("protocol").disable();
            this.deviceForm.get("deviceType").disable();
            this.setupCreateFormDependencies();
        }
    }

    private createFileFiltersForm() {
        this.fileFiltersForm = this.formBuilder.group({
            source: [null],
            startDate: [null],
            endDate: [null],
        });
    }

    private updateForm() {
        if (this.pageType === "edit") {
            this.deviceForm.get("id").setValue(this.device.id);
            this.deviceForm.get("serialNumber").disable();
            this.deviceForm.get("model").disable();
            this.deviceForm.get("manufacturer").disable();
            this.deviceForm.get("protocol").disable();

            this.deviceForm.valueChanges.pipe(debounce(() => timer(this.updatingModel ? 0 : 1000))).subscribe((_val) => {
                if (this.updatingModel) return;
                this.save();
            });
        }
    }

    private updateModel() {
        this.updatingModel = true;
        if (this.device.id) {
            this.pageType = "edit";
            this.imageUrl = this.device.picture;
        }
        this.error = null;
        this.updatingModel = false;
    }

    private updateManufacturerData() {
        this.manufacturerService.getAll().subscribe(
            (data) => {
                this.updatingModel = true;
                this.manufacturers =
                    this.pageType === "edit" ? data : data.filter((manufacturer) => this.isCatalogManufacturer(manufacturer.name));

                if (this.pageType === "edit") {
                    const manufacturer = this.manufacturers.find((obj) => obj.id === this.device.manufacturer);
                    const protocol = manufacturer.protocols.find((obj) => {
                        return obj.name === this.device.protocol.name && obj.endpoint === this.device.protocol.endpoint;
                    });
                    this.selectedManufacturer = manufacturer;
                    this.deviceForm.get("manufacturer").setValue(manufacturer);
                    this.deviceForm.get("protocol").setValue(protocol);
                }
                setTimeout(() => {
                    this.updatingModel = false;
                }, 10);
            },
            (error) => {
                this.toastr.error("Error: " + error.message);
                console.log(error);
            },
        );
    }

    private setupCreateFormDependencies() {
        this.deviceForm.get("manufacturer").valueChanges.subscribe((manufacturer: ManufacturerModel | null) => {
            this.selectedManufacturer = manufacturer;
            this.availableModels = this.getAvailableModels(manufacturer?.name);

            const protocolControl = this.deviceForm.get("protocol");
            const modelControl = this.deviceForm.get("model");
            const deviceTypeControl = this.deviceForm.get("deviceType");

            protocolControl.reset();
            modelControl.reset();
            deviceTypeControl.reset();

            if (manufacturer) {
                protocolControl.enable({ emitEvent: false });
                modelControl.enable({ emitEvent: false });

                if (manufacturer.protocols?.length === 1) {
                    protocolControl.setValue(manufacturer.protocols[0], {
                        emitEvent: false,
                    });
                }
            } else {
                protocolControl.disable({ emitEvent: false });
                modelControl.disable({ emitEvent: false });
            }
        });

        this.deviceForm.get("model").valueChanges.subscribe((model: string | null) => {
            const catalogEntry = this.availableModels.find((entry) => entry.model === model);
            const preferredProtocol = this.findPreferredProtocol(this.selectedManufacturer, catalogEntry?.defaultProtocol);

            this.deviceForm.get("deviceType").setValue(catalogEntry?.deviceType ?? null, { emitEvent: false });

            if (preferredProtocol) {
                this.deviceForm.get("protocol").setValue(preferredProtocol, {
                    emitEvent: false,
                });
            }
        });
    }

    private findPreferredProtocol(manufacturer: ManufacturerModel | null, protocolHint: string | undefined): Protocol | undefined {
        if (!manufacturer?.protocols?.length || !protocolHint) {
            return undefined;
        }

        const normalizedHint = this.normalizeProtocolValue(protocolHint);

        return manufacturer.protocols.find((protocol) => {
            const normalizedName = this.normalizeProtocolValue(protocol.name);
            const normalizedEndpoint = this.normalizeProtocolValue(protocol.endpoint);

            return normalizedName === normalizedHint || normalizedEndpoint.includes(normalizedHint);
        });
    }

    private isCatalogManufacturer(name: string | null | undefined) {
        return !!this.getCatalogManufacturerName(name);
    }

    private getAvailableModels(name: string | null | undefined) {
        const catalogManufacturerName = this.getCatalogManufacturerName(name);
        return catalogManufacturerName ? this.deviceModelCatalog[catalogManufacturerName].models : [];
    }

    private getCatalogManufacturerName(name: string | null | undefined) {
        const normalizedName = this.normalizeManufacturerName(name);
        return Object.keys(this.deviceModelCatalog).find((manufacturerName) =>
            this.matchesCatalogManufacturer(manufacturerName, normalizedName),
        );
    }

    private matchesCatalogManufacturer(manufacturerName: string, normalizedName: string) {
        const catalogEntry: DeviceManufacturerCatalogEntry = this.deviceModelCatalog[manufacturerName];
        const supportedNames = [manufacturerName, ...(catalogEntry.aliases || [])];

        return supportedNames.some((supportedName) => this.normalizeManufacturerName(supportedName) === normalizedName);
    }

    private normalizeManufacturerName(name: string | null | undefined) {
        return this.normalizeProtocolValue(name);
    }

    private normalizeProtocolValue(value: string | null | undefined) {
        return (value || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-zA-Z0-9]/g, "")
            .toLowerCase();
    }

    public getCloudinaryImage(
        publicId: string | null | undefined,
        width: number | null = null,
        height: number | null = null,
        crop: "fill" | "limit" = "limit",
    ): CloudinaryImage | null {
        const id = (publicId || "").trim();
        if (!id || id.startsWith("http://") || id.startsWith("https://") || id.startsWith("data:")) {
            return null;
        }

        const key = `${id}|${width ?? ""}|${height ?? ""}|${crop}`;
        const cached = this.cloudinaryImageCache.get(key);
        if (cached) {
            return cached;
        }

        const image = this.cloudinary.image(id).format("auto").quality("auto");

        if (width !== null || height !== null) {
            const resizeAction = crop === "fill" ? fill() : limitFit();
            if (width !== null) {
                resizeAction.width(width);
            }
            if (height !== null) {
                resizeAction.height(height);
            }
            image.resize(resizeAction);
        }

        this.cloudinaryImageCache.set(key, image);
        return image;
    }

    private loadFiles(filters = this.resolveFileFilters()) {
        if (!this.device?.id) {
            return;
        }

        if (this.hasInvalidFileDateRange(filters)) {
            this.files = [];
            this.filesTotal = 0;
            this.cdr.detectChanges();
            return;
        }

        this.deviceService.getFiles(this.device.id, this.buildFilesRequest(filters)).subscribe(
            (response: PaginationResponse<DeviceFileModel>) => {
                this.files = response.data.map((file) => ({
                    ...file,
                    uploadDate: file.uploadDate ? new Date(file.uploadDate) : undefined,
                    originalDate: file.originalDate ? new Date(file.originalDate) : undefined,
                }));
                this.filesTotal = response.total;
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    private buildFilesRequest(filters: ResolvedFileFilters): DeviceFilesRequest {
        return {
            offset: this.filesPageIndex * this.filesPageSize,
            size: this.filesPageSize,
            ...(filters.source ? { source: filters.source } : {}),
            ...(filters.startDate ? { startDate: this.toStartOfDay(filters.startDate).toISOString() } : {}),
            ...(filters.endDate ? { endDate: this.toEndOfDay(filters.endDate).toISOString() } : {}),
        };
    }

    private resolveFileFilters(): ResolvedFileFilters {
        const formValues = this.fileFiltersForm.getRawValue();

        return {
            source: (formValues.source as DeviceFileSourceFilter | null) ?? null,
            startDate: this.toNativeDate(formValues.startDate),
            endDate: this.toNativeDate(formValues.endDate),
        };
    }

    private hasInvalidFileDateRange(filters: ResolvedFileFilters): boolean {
        return !!filters.startDate && !!filters.endDate && filters.startDate.getTime() > filters.endDate.getTime();
    }

    private resetFileSelection() {
        this.selectedFileIds.clear();
        this.selectedFileRevisions.clear();
    }

    private toStartOfDay(date: Date): Date {
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
    }

    private toEndOfDay(date: Date): Date {
        const normalized = new Date(date);
        normalized.setHours(23, 59, 59, 999);
        return normalized;
    }

    private toNativeDate(value: FileFilterDateValue): Date | null {
        if (!value) {
            return null;
        }

        if (moment.isMoment(value)) {
            return value.isValid() ? value.toDate() : null;
        }

        const normalized = value instanceof Date ? value : new Date(value);
        return Number.isNaN(normalized.getTime()) ? null : normalized;
    }

    private getZipFilename(contentDisposition: string | null): string {
        const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/i);
        if (filenameMatch?.[1]) {
            return filenameMatch[1];
        }

        const deviceIdentifier = (this.device?.serialNumber || this.device?.id || "device").replace(/[^a-zA-Z0-9]+/g, "-");
        return `svantek-files-${deviceIdentifier}.zip`;
    }

    private triggerBlobDownload(blob: Blob, filename: string) {
        const objectUrl = URL.createObjectURL(blob);
        const downloadLink = document.createElement("a");

        downloadLink.href = objectUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();

        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }
}
